import CircularProgress from '@material-ui/core/CircularProgress'
import React, { memo, useCallback } from 'react'
import { useTreeViewStyles } from '../../styles/useTreeViewStyles'

import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import Add from '@material-ui/icons/Add'
import { useAddContentActions } from 'components/WorkspaceEditor/hooks/useAddContentActions'
import { DataNode } from 'components/WorkspaceEditor/types'
import { tryGetShortcutKey } from '../../utils/shortcutKeyUtils'
import { isDataNodeMOPTArticle } from 'components/WorkspaceEditor/features/moptArticleRestriction'

/** Props for rendering add icon of <TreeView /> component */
export interface AddIconProps {
    /** Node to add */
    node: DataNode
    /** A flag whether the node is loading */
    isLoading: boolean
    /** Test id for the add icon */
    'data-testid'?: string
    /** Whether the add functionality is disabled */
    isDisabled?: boolean
}

/**
 * Renders the add icon property of <TreeView /> component
 * @param {AddIconProps} props - Props for rendering add icon
 * @returns {ReactElement} Node add icon
 */
export const AddIcon = memo<AddIconProps>(
    ({
        node,
        isLoading = false,
        'data-testid': dataTestId = 'add-icon',
        isDisabled = false,
    }) => {
        const styles = useTreeViewStyles()
        const {
            canAddContent,
            addContentAfterSibling,
            isAddDisabled: hookIsAddDisabled,
            getTooltipText,
        } = useAddContentActions()

        // Check if the current node is a MOPT article (for visual feedback)
        const isNodeMOPTArticle = isDataNodeMOPTArticle(node)

        // Combine hook's disabled state with component's disabled props and MOPT check
        const isAddDisabled =
            isDisabled || node.isHidden || hookIsAddDisabled || isNodeMOPTArticle

        const handleClick = useCallback(() => {
            if (canAddContent(node, 'AddIcon')) {
                addContentAfterSibling(node)
            }
        }, [canAddContent, addContentAfterSibling, node])

        const handleKeyDown = useCallback(
            (e: React.KeyboardEvent) => {
                const enterKey = tryGetShortcutKey('Enter', e, true)
                const spaceKey = tryGetShortcutKey('Space', e, true)

                if (enterKey || spaceKey) {
                    e.preventDefault()
                    handleClick()
                }
            },
            [handleClick]
        )

        const tooltipText = getTooltipText(node)

        if (isLoading) {
            return (
                <CircularProgress
                    data-testid={dataTestId}
                    className={styles.switcherLoadingIcon}
                    size={16}
                    aria-label="Loading"
                    aria-busy="true"
                    role="status"
                />
            )
        }

        return (
            <HoverTooltip
                text={tooltipText}
                className={
                    isAddDisabled ? styles.disabledAddIcon : styles.addIcon
                }
                placements={['top', 'bottom']}
            >
                <Add
                    data-testid="addButton"
                    fontSize="inherit"
                    onClick={handleClick}
                    aria-label={tooltipText}
                    aria-disabled={isAddDisabled}
                    role="button"
                    tabIndex={isAddDisabled ? -1 : 0}
                    onKeyDown={handleKeyDown}
                />
            </HoverTooltip>
        )
    }
)
